import { ExportModel, type ExportError, type PostExport, type PostExportResponse } from '@/models/export';
import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';
import { useStudyStore } from './study';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import ExportService from '@/services/exportService';
import { LoadingStateModel } from '@/models/loadingState';
import { useConnnectionStore } from './connection';
import { useRoute } from 'vue-router';


export const useExportStore = defineStore('export', () => {

  const studyStore = useStudyStore();
  const exportList = ref<ExportModel[]>([]);
  const route = useRoute();

  const isExportLoading = reactive(new LoadingStateModel({isLoading: true}));
  const deleteExportsLoader = reactive(new LoadingStateModel({isLoading: false}));

  const getStudyExports = async (exportResponse?: PostExportResponse) => {
    if (route.params.studyIdentifier) {
      studyStore.selectedStudyId = route.params.studyIdentifier as string;
    }
    if (studyStore.selectedStudyId) {
      try {
        isExportLoading.startLoading();
        const exports = await ExportService.getStudyExports(studyStore.selectedStudyId);
        if (exportResponse) {
          exports.forEach(exportItem => {
            if (exportItem.identifier === exportResponse.identifier) {
              exportItem.preparing = true;
            }
          });
          exportList.value = exports;
        } else {
          exportList.value = exports;
        }
      } catch (error) {
        isExportLoading.setError(error);
      } finally {
        isExportLoading.stopLoadingAndFinish();
      }
    }
  };


  const tableColumns: TableColumnModel[] = [
    {
      id: 1,
      header: 'Date',
      key: 'createdAt',
      type: TableColumnType.TEXT_WTIH_ICON,
      icon: {
        icon: 'file-zipper',
        style: 'far'
      },
      sortable: true
    },
    {
      id: 2,
      header: 'Form',
      type: TableColumnType.TEXT,
      key: 'exportFormNames',
      sortable: true
    },
    {
      id: 3,
      header: 'File Format',
      type: TableColumnType.TEXT,
      key: 'contentType',
      sortable: true
    },
    {
      id: 4,
      header: 'Created By',
      type: TableColumnType.USER,
      key: 'creatorNames',
      sortable: true
    },
    {
      id: 5,
      type: TableColumnType.BUTTON_WITH_ICON,
      key: 'Download',
      icon: {
        icon: 'download',
        style: 'far'
      },
      sortable: false
    }
  ];


  const getExportLink = async (identifier: string) => {
    const exportLink = await ExportService.getExportSignedUrl(identifier);
    return exportLink;
  };

  
  const createExportLoader = reactive(new LoadingStateModel({isLoading: false}));
  const createStudyExport = async (postsExport: PostExport) => {
    if (studyStore.selectedStudyId) {
      try {
        createExportLoader.startLoading();
        const exportResponse = await ExportService.createStudyExport(studyStore.selectedStudyId!, postsExport);
        createExportLoader.setSuccess(`New export created!`);
        if (exportResponse) {
          getStudyExports(exportResponse);

          const connectionStore = useConnnectionStore();
          connectionStore.connect();
          connectionStore.bindEvents();
        }
      } catch (error) {
        createExportLoader.setError(error);
      } finally {
        createExportLoader.stopLoadingAndFinish();
      }
    }
  };

  const setExportFinishedStatus = (identifier: string, error?: ExportError) => {
    const exports = exportList.value;
    exports.forEach(exportItem => {
      if (exportItem.identifier === identifier) {
        if (error) {
          exportItem.preparing = false;
          exportItem.error = error;
        } else {
          exportItem.preparing = false;
          exportItem.storedAt = new Date();
        }
      }
    });
    exportList.value = exports;
  };

  const deleteExports = async (selectedExports: ExportModel[]) => {
    deleteExportsLoader.startLoading();
    try {
      const filteredIds = selectedExports.map(exportItem => exportItem.identifier);
      await ExportService.deleteExports(filteredIds);
      if (selectedExports.length > 1) {
        deleteExportsLoader.setSuccess(`${selectedExports.length} data exports deleted!`);
      } else {
        deleteExportsLoader.setSuccess('Data export deleted!');
      }
    } catch (error) {
      deleteExportsLoader.setError(error);
    } finally {
      deleteExportsLoader.stopLoadingAndFinish();
    }
  };

  const retryExport = async (id: string) => {
    await ExportService.retryExport(id);
    await getStudyExports(exportList.value.filter(exportItem => exportItem.identifier === id)[0]);

    const connectionStore = useConnnectionStore();
    connectionStore.connect();
    connectionStore.bindEvents();
  };

  return {
    exportList,
    getStudyExports,
    tableColumns,
    isExportLoading,
    getExportLink,
    createStudyExport,
    createExportLoader,
    setExportFinishedStatus,
    deleteExportsLoader,
    deleteExports,
    retryExport
  };
});
